import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { 
  Recycle, 
  Menu, 
  X, 
  User, 
  LogOut, 
  Package, 
  ClipboardList,
  Shield,
  MapPin,
  HelpCircle,
  Home,
  Plus,
  Leaf
} from "lucide-react";
import { useState } from "react";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/catalogo';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const navLinks = [
    { to: "/catalogo", label: "Catálogo", icon: Package },
    { to: "/impacto", label: "Impacto", icon: Leaf },
    { to: "/localizacion", label: "Localización", icon: MapPin },
    { to: "/faqs", label: "FAQs", icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-10 h-10 bg-[#374B67] rounded-lg flex items-center justify-center">
                <Recycle className="w-6 h-6 text-white" />
              </div>
              <div className="hidden sm:block">
                <span className="font-bold text-[#374B67] text-lg">Reutiliza</span>
                <span className="text-[#69C7EA] font-semibold text-lg"> Majadahonda</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-slate-600 hover:text-[#374B67] font-medium transition-colors"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* User Actions */}
            <div className="flex items-center gap-3">
              {user ? (
                <>
                  {/* Points Badge */}
                  <div className="hidden sm:flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-semibold" data-testid="points-badge">
                    <span>{user.points || 0}</span>
                    <span>Pts</span>
                  </div>

                  {/* Add Product Button */}
                  <Link to="/anadir-producto">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="hidden sm:flex border-[#374B67] text-[#374B67] hover:bg-[#374B67] hover:text-white"
                      data-testid="add-product-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Añadir
                    </Button>
                  </Link>

                  {/* User Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 bg-[#374B67] rounded-full flex items-center justify-center text-white text-sm font-semibold">
                            {user.name?.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-3 py-2">
                        <p className="font-semibold text-slate-900">{user.name}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                        <div className="mt-1 flex items-center gap-1 text-green-600 text-sm font-medium">
                          <span>{user.points || 0} puntos</span>
                        </div>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/perfil" className="flex items-center gap-2 cursor-pointer" data-testid="menu-profile">
                          <User className="w-4 h-4" />
                          Mi Perfil
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/mis-reservas" className="flex items-center gap-2 cursor-pointer" data-testid="menu-reservations">
                          <ClipboardList className="w-4 h-4" />
                          Mis Reservas
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/anadir-producto" className="flex items-center gap-2 cursor-pointer sm:hidden" data-testid="menu-add-product">
                          <Plus className="w-4 h-4" />
                          Añadir Producto
                        </Link>
                      </DropdownMenuItem>
                      {user.is_admin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link to="/admin" className="flex items-center gap-2 cursor-pointer text-[#374B67]" data-testid="menu-admin">
                              <Shield className="w-4 h-4" />
                              Panel Admin
                            </Link>
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="menu-logout">
                        <LogOut className="w-4 h-4 mr-2" />
                        Cerrar Sesión
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" className="text-[#374B67]" data-testid="login-btn">
                      Iniciar Sesión
                    </Button>
                  </Link>
                  <Link to="/registro" className="hidden sm:block">
                    <Button className="bg-[#374B67] hover:bg-[#2C3E56] rounded-full" data-testid="register-btn">
                      Regístrate
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#374B67] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <Recycle className="w-6 h-6 text-[#69C7EA]" />
                </div>
                <div>
                  <span className="font-bold text-white text-lg">Reutiliza</span>
                  <span className="text-[#69C7EA] font-semibold text-lg"> Majadahonda</span>
                </div>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed max-w-md">
                Plataforma de intercambio de objetos del Ayuntamiento de Majadahonda. 
                Dale una segunda vida a los objetos que ya no utilizas y contribuye a la economía circular.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Enlaces</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/catalogo" className="text-slate-300 hover:text-white transition-colors">Catálogo</Link></li>
                <li><Link to="/faqs" className="text-slate-300 hover:text-white transition-colors">Preguntas Frecuentes</Link></li>
                <li><Link to="/localizacion" className="text-slate-300 hover:text-white transition-colors">Localización</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-white mb-4">Punto Limpio</h4>
              <address className="text-slate-300 text-sm not-italic leading-relaxed">
                Carretera de El Plantío, 47<br />
                28221 Majadahonda, Madrid<br />
                <span className="text-[#69C7EA]">Gestionado por Valoriza</span>
              </address>
            </div>
          </div>

          {/* Logos & Copyright */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/22/Escudo_de_Majadahonda.svg/1200px-Escudo_de_Majadahonda.svg.png" 
                  alt="Ayuntamiento de Majadahonda" 
                  className="h-12 object-contain"
                />
                <div className="h-8 w-px bg-white/20"></div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-600 rounded flex items-center justify-center">
                    <span className="text-white font-bold text-xs">V</span>
                  </div>
                  <span className="text-sm text-slate-300">Valoriza</span>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-slate-400 text-sm">
                  © {new Date().getFullYear()} Reutiliza Majadahonda - Ayuntamiento de Majadahonda
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  Gestionado por Valoriza Servicios Medioambientales
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
